import { useEffect, useState } from 'react';

const canUseMatchMedia = () => typeof window !== 'undefined' && typeof window.matchMedia === 'function';

/**
 * React hook that subscribes to a CSS media query and reports whether it currently matches.
 * Useful for keeping responsive logic in sync with Tailwind breakpoints.
 */
export function useMediaQuery(query: string): boolean {
  const getMatch = () => (canUseMatchMedia() ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState<boolean>(getMatch);

  useEffect(() => {
    if (!canUseMatchMedia()) {
      return;
    }

    const mediaQueryList = window.matchMedia(query);
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Ensure we sync with the most recent value on mount
    setMatches(mediaQueryList.matches);

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', handleChange);
      return () => mediaQueryList.removeEventListener('change', handleChange);
    }

    // Fallback for older browsers
    mediaQueryList.addListener(handleChange);
    return () => mediaQueryList.removeListener(handleChange);
  }, [query]);

  return matches;
}
